import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  FileText, 
  Download, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  Search,
  Eye,
  Lock,
  Hash,
  Database,
  BarChart3,
  Calendar,
  Building2,
  User,
  FileCheck,
  ClipboardCheck,
  RefreshCw,
  ChevronRight,
  Upload,
  Stamp,
  Scale,
  Link2,
  MessageSquare,
  Printer
} from 'lucide-react';
import { apiFetch } from '../services/api_service';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auditor Login Component
const AuditorLogin = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ auditor_id: '', firm_code: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const data = await apiFetch(`${API}/auditor/login`, { method: 'POST', body: credentials });
    if (data?._error) {
      setError(data.message || 'Invalid credentials');
      setLoading(false);
      return;
    }
    if (data.access_token) {
      localStorage.setItem('auditor_token', data.access_token);
      localStorage.setItem('auditor_info', JSON.stringify(data.auditor));
      onLogin(data);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ClipboardCheck className="text-white" size={32} />
          </div>
          <h1 className="text-2xl font-bold text-white">External Auditor Portal</h1>
          <p className="text-indigo-300 mt-2">ESG Assurance & Verification Platform</p>
        </div>
        
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-indigo-200 mb-2">Auditor ID</label>
              <input
                type="text"
                value={credentials.auditor_id}
                onChange={(e) => setCredentials({ ...credentials, auditor_id: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-indigo-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Your auditor certification ID"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-indigo-200 mb-2">Firm Code</label>
              <input
                type="text"
                value={credentials.firm_code}
                onChange={(e) => setCredentials({ ...credentials, firm_code: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-indigo-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Audit firm code"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-indigo-200 mb-2">Password</label>
              <input
                type="password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-indigo-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Your password"
                required
              />
            </div>
            
            {error && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-300 text-sm">
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium flex items-center justify-center gap-2"
            >
              {loading ? (
                <RefreshCw size={18} className="animate-spin" />
              ) : (
                <Lock size={18} />
              )}
              {loading ? 'Authenticating...' : 'Access Portal'}
            </button>
          </form>
          
          <div className="mt-6 pt-6 border-t border-white/10">
            <p className="text-xs text-indigo-300 text-center">
              Access client ESG data, verify disclosures, and provide assurance opinions.
            </p>
          </div>
        </div>
        
        <div className="mt-6 flex items-center justify-center gap-4 text-indigo-400 text-xs">
          <span>ISAE 3000</span>
          <span>•</span>
          <span>AA1000AS</span>
          <span>•</span>
          <span>CSRD Art. 34</span>
        </div>
      </div>
    </div>
  );
};

// Auditor Dashboard Component
const AuditorDashboard = ({ auditorInfo, onLogout }) => {
  const [activeTab, setActiveTab] = useState('assignments');
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  useEffect(() => {
    loadAuditorData();
  }, []);

  const loadAuditorData = async () => {
    setLoading(true);
    try {
      // Load auditor assignments
      setAssignments(generateMockAssignments());
    } catch (err) {
      console.error('Failed to load auditor data:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateMockAssignments = () => [
    { 
      id: '1', 
      company: 'TechCorp Industries', 
      scope: 'Limited Assurance - ESRS', 
      status: 'in_progress', 
      deadline: '2026-03-15',
      progress: 65,
      findings: 3,
      evidence_count: 47
    },
    { 
      id: '2', 
      company: 'GreenEnergy Ltd', 
      scope: 'Reasonable Assurance - Climate', 
      status: 'review', 
      deadline: '2026-02-28',
      progress: 90,
      findings: 1,
      evidence_count: 82
    },
    { 
      id: '3', 
      company: 'ManufactureCo', 
      scope: 'Limited Assurance - ESRS', 
      status: 'pending', 
      deadline: '2026-04-01',
      progress: 15,
      findings: 0,
      evidence_count: 12
    },
  ];

  const tabs = [
    { id: 'assignments', label: 'Assignments', icon: ClipboardCheck },
    { id: 'workpapers', label: 'Workpapers', icon: FileText },
    { id: 'evidence', label: 'Evidence Review', icon: FileCheck },
    { id: 'findings', label: 'Findings', icon: AlertTriangle },
    { id: 'opinions', label: 'Opinions', icon: Stamp }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-indigo-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                <ClipboardCheck size={20} />
              </div>
              <div>
                <h1 className="font-bold">Auditor Assurance Portal</h1>
                <p className="text-sm text-indigo-300">ESG Verification & Assurance</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium">{auditorInfo?.name || 'Auditor'}</p>
                <p className="text-xs text-indigo-300">{auditorInfo?.firm || 'Audit Firm'}</p>
              </div>
              <button
                onClick={onLogout}
                className="px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20 text-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 flex items-center gap-2 text-sm font-medium rounded-t-lg transition-colors ${
                  activeTab === tab.id
                    ? 'bg-slate-50 text-slate-900'
                    : 'text-indigo-300 hover:text-white hover:bg-white/10'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="animate-spin text-indigo-600" size={32} />
          </div>
        ) : (
          <>
            {activeTab === 'assignments' && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl p-5 border border-slate-200">
                    <ClipboardCheck className="text-indigo-500 mb-3" size={20} />
                    <p className="text-2xl font-bold text-slate-900">{assignments.length}</p>
                    <p className="text-sm text-slate-500">Active Assignments</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-slate-200">
                    <Clock className="text-amber-500 mb-3" size={20} />
                    <p className="text-2xl font-bold text-slate-900">
                      {assignments.filter(a => a.status === 'in_progress').length}
                    </p>
                    <p className="text-sm text-slate-500">In Progress</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-slate-200">
                    <AlertTriangle className="text-red-500 mb-3" size={20} />
                    <p className="text-2xl font-bold text-slate-900">
                      {assignments.reduce((sum, a) => sum + a.findings, 0)}
                    </p>
                    <p className="text-sm text-slate-500">Open Findings</p>
                  </div>
                  <div className="bg-white rounded-xl p-5 border border-slate-200">
                    <FileCheck className="text-emerald-500 mb-3" size={20} />
                    <p className="text-2xl font-bold text-slate-900">
                      {assignments.reduce((sum, a) => sum + a.evidence_count, 0)}
                    </p>
                    <p className="text-sm text-slate-500">Evidence Items</p>
                  </div>
                </div>

                {/* Assignments List */}
                <div className="bg-white rounded-xl border border-slate-200">
                  <div className="p-4 border-b border-slate-200">
                    <h2 className="font-semibold text-slate-900">Audit Assignments</h2>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {assignments.map((assignment, idx) => (
                      <div 
                        key={idx} 
                        className="p-4 hover:bg-slate-50 cursor-pointer"
                        onClick={() => setSelectedAssignment(assignment)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              assignment.status === 'in_progress' ? 'bg-amber-100' :
                              assignment.status === 'review' ? 'bg-emerald-100' : 'bg-slate-100'
                            }`}>
                              <Building2 size={20} className={
                                assignment.status === 'in_progress' ? 'text-amber-600' :
                                assignment.status === 'review' ? 'text-emerald-600' : 'text-slate-500'
                              } />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{assignment.company}</p>
                              <p className="text-sm text-slate-500">{assignment.scope}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-6">
                            <div className="text-right">
                              <p className="text-sm text-slate-500">Progress</p>
                              <div className="flex items-center gap-2">
                                <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-indigo-500"
                                    style={{ width: `${assignment.progress}%` }}
                                  />
                                </div>
                                <span className="text-sm font-medium">{assignment.progress}%</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-slate-500">Deadline</p>
                              <p className="text-sm font-medium text-slate-900">{assignment.deadline}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-slate-500">Findings</p>
                              <p className={`text-sm font-medium ${assignment.findings > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                                {assignment.findings}
                              </p>
                            </div>
                            <ChevronRight className="text-slate-400" size={20} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'workpapers' && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4">Audit Workpapers</h2>
                <p className="text-sm text-slate-500 mb-4">
                  Document your audit procedures, testing results, and conclusions.
                </p>
                <div className="grid grid-cols-3 gap-4">
                  {['Planning', 'Risk Assessment', 'Substantive Testing', 'Controls Testing', 'Conclusions', 'Review Notes'].map((section, idx) => (
                    <div key={idx} className="p-4 border border-slate-200 rounded-lg hover:bg-slate-50 cursor-pointer">
                      <FileText className="text-indigo-500 mb-2" size={20} />
                      <p className="font-medium text-slate-900">{section}</p>
                      <p className="text-xs text-slate-500 mt-1">Click to open</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'evidence' && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4">Evidence Review</h2>
                <p className="text-sm text-slate-500 mb-4">
                  Review and verify supporting evidence for ESG disclosures.
                </p>
                <div className="space-y-3">
                  {[
                    { name: 'Emissions Calculation Spreadsheet', type: 'Excel', verified: true },
                    { name: 'Energy Invoices Q1-Q4', type: 'PDF', verified: true },
                    { name: 'Scope 3 Supplier Data', type: 'CSV', verified: false },
                    { name: 'HR Workforce Statistics', type: 'Report', verified: true },
                    { name: 'Board ESG Policy Document', type: 'PDF', verified: false },
                  ].map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="text-slate-400" size={18} />
                        <div>
                          <p className="font-medium text-slate-900">{doc.name}</p>
                          <p className="text-xs text-slate-500">{doc.type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {doc.verified ? (
                          <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full flex items-center gap-1">
                            <CheckCircle2 size={12} />
                            Verified
                          </span>
                        ) : (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                            Pending Review
                          </span>
                        )}
                        <button className="p-1.5 hover:bg-white rounded">
                          <Eye size={16} className="text-slate-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'findings' && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4">Audit Findings</h2>
                <div className="space-y-4">
                  {[
                    { severity: 'high', title: 'Incomplete Scope 3 documentation', company: 'TechCorp Industries', status: 'open' },
                    { severity: 'medium', title: 'Minor calculation discrepancy', company: 'TechCorp Industries', status: 'resolved' },
                    { severity: 'low', title: 'Policy document outdated', company: 'GreenEnergy Ltd', status: 'open' },
                  ].map((finding, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                      finding.severity === 'high' ? 'bg-red-50 border-red-500' :
                      finding.severity === 'medium' ? 'bg-amber-50 border-amber-500' :
                      'bg-blue-50 border-blue-500'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">{finding.title}</p>
                          <p className="text-sm text-slate-500">{finding.company}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          finding.status === 'open' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {finding.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'opinions' && (
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="font-semibold text-slate-900 mb-4">Assurance Opinions</h2>
                <p className="text-sm text-slate-500 mb-4">
                  Draft and issue assurance opinions for completed audits.
                </p>
                <div className="p-8 bg-slate-50 rounded-lg text-center">
                  <Stamp className="mx-auto text-slate-400 mb-2" size={32} />
                  <p className="text-slate-600">Select an assignment to draft an opinion</p>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

// Main Component
const AuditorPortal = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [auditorInfo, setAuditorInfo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('auditor_token');
    const info = localStorage.getItem('auditor_info');
    if (token && info) {
      setIsAuthenticated(true);
      setAuditorInfo(JSON.parse(info));
    }
  }, []);

  const handleLogin = (data) => {
    setIsAuthenticated(true);
    setAuditorInfo(data.auditor);
  };

  const handleLogout = () => {
    localStorage.removeItem('auditor_token');
    localStorage.removeItem('auditor_info');
    setIsAuthenticated(false);
    setAuditorInfo(null);
  };

  if (!isAuthenticated) {
    return <AuditorLogin onLogin={handleLogin} />;
  }

  return <AuditorDashboard auditorInfo={auditorInfo} onLogout={handleLogout} />;
};

export default AuditorPortal;
